import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Initialize from localStorage cache to avoid flash of hardcoded content
  const [settings, setSettings] = useState(() => {
    const cached = localStorage.getItem('app_settings');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          app_name: parsed.app_name || '',
          company_name: parsed.company_name || parsed.app_name || '',
          contact_email: parsed.contact_email || '',
          facebook_url: '',
          twitter_url: '',
          instagram_url: '',
          linkedin_url: ''
        };
      } catch (e) {
        // Fall through to default
      }
    }
    return {
      app_name: '',
      company_name: '',
      contact_email: '',
      facebook_url: '',
      twitter_url: '',
      instagram_url: '',
      linkedin_url: ''
    };
  });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/general-settings/public`);
      if (response.data) {
        setSettings({
          app_name: response.data.app_name || 'BettingTracker.Pro',
          company_name: response.data.company_name || response.data.app_name || 'BettingTracker.Pro',
          contact_email: response.data.contact_email || '',
          facebook_url: response.data.facebook_url || '',
          twitter_url: response.data.twitter_url || '',
          instagram_url: response.data.instagram_url || '',
          linkedin_url: response.data.linkedin_url || ''
        });
        setIsLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching footer settings:', error);
      // Still show footer with cached/empty values
      setIsLoaded(true);
    }
  };

  const socialLinks = [
    { url: settings.facebook_url, icon: Facebook, label: 'Facebook' },
    { url: settings.twitter_url, icon: Twitter, label: 'Twitter' },
    { url: settings.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: settings.linkedin_url, icon: Linkedin, label: 'LinkedIn' }
  ].filter(link => link.url && link.url.trim() !== '');

  // Don't render footer until settings are loaded to prevent flash
  if (!isLoaded && !settings.company_name) {
    return (
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto h-10" />
      </footer>
    );
  }

  return (
    <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Left: Copyright */}
          <div className="text-gray-400 text-sm">
            © {new Date().getFullYear()}{' '}
            {user ? (
              <span 
                onClick={() => navigate('/dashboard')}
                className="cursor-pointer hover:text-white transition-colors"
              >
                {settings.company_name || 'BettingTracker.Pro'}
              </span>
            ) : (
              <a 
                href="/"
                className="hover:text-white transition-colors"
              >
                {settings.company_name || 'BettingTracker.Pro'}
              </a>
            )}
            . All rights reserved.
          </div>
          
          {/* Right: Social Links, Email, Privacy, Terms */}
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4">
            {/* Social Media Icons */}
            {socialLinks.map((social, index) => {
              const Icon = social.icon;
              return (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  aria-label={social.label}
                >
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
            
            {/* Separator if we have social links and email/links */}
            {socialLinks.length > 0 && (settings.contact_email || true) && (
              <span className="text-gray-600">|</span>
            )}
            
            {/* Contact Email */}
            {settings.contact_email && (
              <>
                <a 
                  href={`mailto:${settings.contact_email}`}
                  className="text-gray-400 hover:text-blue-400 text-sm transition-colors flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">{settings.contact_email}</span>
                </a>
                <span className="text-gray-600">|</span>
              </>
            )}
            
            {/* Advertising Link */}
            <Link 
              to="/advertising" 
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              Advertising
            </Link>
            <span className="text-gray-600">|</span>
            
            {/* GDPR Link */}
            <Link 
              to="/gdpr" 
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              GDPR
            </Link>
            <span className="text-gray-600">|</span>
            
            {/* Privacy & Terms Links */}
            <Link 
              to="/privacy-policy" 
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/terms-of-use" 
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
